/**
 * Chunked upload transport.
 *
 * The API sits behind a reverse proxy that answers any request body over about
 * 1 MB with its own "413 Request Entity Too Large", before Node — and so
 * before multer's 10 MB allowance — ever sees it. That is why a 1.1 MB PDF
 * failed on the live host while the same file uploaded fine against a bare dev
 * server, and why the onboarding wizard's ~17 MB combined submit failed while
 * every individual document sat well under the cap.
 * features/backgrounds/mediaStore.js dodges the same wall by re-encoding images
 * under 900 KB, which does nothing for a PDF.
 *
 * So a large file goes up in slices that each clear the proxy on their own, and
 * the caller hands the resulting id to whichever endpoint claims the file.
 * Server-side counterpart: backend/services/chunkedUploads.js.
 */
import axios from 'axios';
import { API_URL } from './api';

const base = `${API_URL}/api/uploads`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Files at or under this size can still go in one request wherever an endpoint
// accepts them directly — unchanged behaviour, and no extra round trips.
export const DIRECT_UPLOAD_MAX_BYTES = 512 * 1024;

/**
 * Push a file up in slices and return its upload id.
 *
 * Serial on purpose. The server appends chunks in order into one file, so two
 * in flight at once would interleave; and parallelism buys little when the
 * bottleneck is a proxy that wants small bodies anyway.
 *
 * @param {File} file
 * @param {(fraction:number)=>void} [onProgress] 0..1, excludes the claim step
 * @returns {Promise<string>} uploadId
 */
export async function uploadFileInChunks(file, onProgress) {
  const init = await axios.post(
    `${base}/init`,
    { name: file.name, size: file.size, type: file.type },
    { headers: authHeaders() },
  );
  // chunkBytes comes from the server so the safe slice size lives in one place.
  const { uploadId, chunkBytes } = init.data;

  try {
    const total = Math.ceil(file.size / chunkBytes);
    for (let index = 0; index < total; index += 1) {
      const form = new FormData();
      // `index` MUST be appended before the blob: multer streams a multipart
      // body in order, so a text field sent after the file part is not yet in
      // req.body when the handler reads it.
      form.append('index', index);
      form.append('chunk', file.slice(index * chunkBytes, (index + 1) * chunkBytes));
      await axios.post(`${base}/${uploadId}/chunk`, form, { headers: authHeaders() });
      onProgress?.((index + 1) / total);
    }
    return uploadId;
  } catch (err) {
    // Free the server's part file now rather than leaving it for the sweeper.
    cancelChunkedUpload(uploadId);
    throw err;
  }
}

/** Abandon an upload the caller is never going to claim. Best effort. */
export function cancelChunkedUpload(uploadId) {
  if (!uploadId) return;
  axios.delete(`${base}/${uploadId}`, { headers: authHeaders() }).catch(() => {});
}
